import {IMocks} from '@graphql-tools/mock';

function typeOf(x: unknown): string {
  return Array.isArray(x) ? 'array' : typeof x;
}

function isScalar(x: unknown): boolean {
  return x == null || !['array', 'object', 'function'].includes(typeOf(x));
}

function mergeMockLists(base: Array<unknown>, override: Array<unknown>): Array<unknown> {
  // merge elements in same position
  return override.map((elem, i) => {
    if (i < base.length) {
      return mergeMocks(base[i], elem);
    } else {
      return elem;
    }
  });
}

function mergeMockObjects(
  base: {[k: string]: unknown},
  override: {[k: string]: unknown}
): {[k: string]: unknown} {
  if (base == null || base.__isMockJSONScalar) {
    return override;
  }
  const merged = {...base, ...override};
  // merge conflicting keys
  Object.keys(base)
    .filter((k) => override.hasOwnProperty(k))
    .forEach((k) => {
      merged[k] = mergeMocks(base[k], override[k]);
    });

  return merged;
}

/**
 * Merges 2 mocks when at least one of them is a function (resolver).
 * Wraps both mocks in a resolver that delays merging till after both mocks have resolved. This enables mock
 * resolvers that actually use their arguments.
 */
function mergeWithResolvers(base: unknown, override: unknown): Function {
  // @ts-expect-error
  return (...args) => {
    const baseValue = typeof base === 'function' ? base(...args) : base;
    const overrideValue = typeof override === 'function' ? override(...args) : override;
    return mergeMocks(baseValue, overrideValue);
  };
}

/**
 * Recursively merges mocks (mocks can be resolver functions or static values), giving precedence to
 * mocks from `override`.
 */
export function mergeMocks(base: unknown, override: unknown): unknown {
  const baseType = typeOf(base);
  const overrideType = typeOf(override);
  // If either side is a resolver, we want to merge on its return value
  if (baseType === 'function' || overrideType === 'function') {
    return mergeWithResolvers(base, override);
  }
  if (baseType !== overrideType || isScalar(override)) {
    return override;
  }
  if (overrideType === 'array') {
    // @ts-expect-error
    return mergeMockLists(base, override);
  }
  if (overrideType === 'object') {
    // @ts-expect-error
    return mergeMockObjects(base, override);
  }
  throw new Error(`unexpected type ${overrideType}`);
}

/**
 * Merges (recursively) `overrides` onto `base` in the order they are provided.
 */
export default function overrideMocksInOrder(base: IMocks, ...overrides: Array<IMocks>): IMocks {
  return overrides.reduce((merged, override) => mergeMocks(merged, override) as IMocks, base);
}
